import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { PartnerRevenueAnalyticsChartPoint } from "@/types/partner-revenue-analytics";
import { formatPartnerRevenueValue } from "@/components/pages/guesteria-partners/utils/guesteriaPartnersFormat";

export type GuesteriaPartnerRevenueChartPoint =
  PartnerRevenueAnalyticsChartPoint;

export type GuesteriaPartnerRevenueChartProps = {
  data: GuesteriaPartnerRevenueChartPoint[];
  currency?: string;
};

export function GuesteriaPartnerRevenueChart({
  data,
  currency = "EUR",
}: GuesteriaPartnerRevenueChartProps): React.JSX.Element {
  const { t } = useTranslation();

  const chartData = useMemo(
    () =>
      data.map((point) => ({
        ...point,
        revenue: point.totalRevenue,
      })),
    [data],
  );

  return (
    <div className="h-[280px] w-full min-w-0">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            className="stroke-border/60"
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11 }}
            className="text-muted-foreground"
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            className="text-muted-foreground"
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) =>
              formatPartnerRevenueValue(Number(value), currency)
            }
            width={56}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const point = payload[0]
                .payload as GuesteriaPartnerRevenueChartPoint & {
                revenue: number;
              };

              return (
                <div className="space-y-1 rounded-md border bg-popover px-2 py-1.5 text-xs shadow-md">
                  <p className="font-medium text-foreground">{point.label}</p>
                  <p className="text-muted-foreground">
                    {t("partners.detail.analyticsTooltipRevenue", {
                      value: formatPartnerRevenueValue(
                        point.totalRevenue,
                        point.currency || currency,
                      ),
                    })}
                  </p>
                  <p className="text-muted-foreground">
                    {t("partners.detail.analyticsTooltipOrders", {
                      count: point.totalOrders,
                    })}
                  </p>
                </div>
              );
            }}
          />
          <Line
            type="monotone"
            dataKey="revenue"
            stroke="#49A432"
            strokeWidth={2}
            dot={{ r: 3, fill: "#49A432", strokeWidth: 0 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
